import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import ApiService from '../services/ApiService';
import { toBase64 } from '../services/toBase64';
import getUser from "../services/decodeJWT";
import { genderOptions, languageOptions, countryOptions, eyeColorOptions, hairColorOptions, ethnicityOptions, experienceOptions, industryOptions, employmentStatusOptions, hobbiesOptions, speakingStyleOptions } from '../constants/config';
import Loading from '../components/Loading';
import Nav from '../components/Nav';
import ConfirmButton from '../components/ConfirmButton';

const EditClone = () => {
    const { id } = useParams();
    const currentUser = getUser();
    const navigate = useNavigate();

    const [disabled, setDisabled] = useState(false)
    const [loading, setLoading] = useState(false);
    const [clonesList, setClonesList] = useState([]);
    const [activeTab, setActiveTab] = useState("profile-edit");

    const tabs = [
        { id: 'profile-edit', label: 'Personal Info', active: true },
        { id: 'physical-edit', label: 'Physical Info' },
        { id: 'character-edit', label: 'Character' },
        { id: 'job-edit', label: 'Professional Info' },
        { id: 'hobbies-edit', label: 'Hobbies' },
        { id: 'speech-edit', label: 'Speaking Style' },
    ];

    const [cloneData, setCloneData] = useState({
        name: '',
        surname: '',
        birthday: '',
        gender: '',
        language: '',
        spouse: [],
        child_of: [],
        city: '',
        country: '',
        description: '',
        picture: {},
        cover: {},
        height: '',
        weight: '',
        eyecolor: '',
        haircolor: '',
        bodytype: '',
        ethnicity: '',
        distinctivemarks: '',
        otherphysicaltraits: '',
        jobtitle: '',
        jobdescription: '',
        experiencelevel: '',
        industry: '',
        employmentstatus: '',
        skills: '',
        hobbies: [],
        hobbiesdescription: '',
        customHobby: '',
        personalitytraits: '',
        behaviors: '',
        interests: '',
        strengths: '',
        weaknesses: '',
        motivations: '',
        emotionalpatterns: '',
        socialpreferences: '',
        copingmechanisms: '',
        speakingstyle: [],
        speakingstyledescription: '',
        customspeakingstyle: '',
    });

    useEffect(() => {
    }, [cloneData])

    const getClone = async () => {
        try {
            setLoading(true);
            const response = await ApiService.get(`/cms/clones?id=${id}`);
            setCloneData(response?.contents?.length && response?.contents[0]?.data);
            setLoading(false);
        } catch (err) {
            console.error("Error during fetching clone data:", err);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const getClones = useCallback(async () => {
        try {
            const response = await ApiService.get(`/cms/clones?limit=100&author=${currentUser?._id}`);
            setClonesList(response.contents || []);
        } catch (err) {
            console.error("Error during getClones:", err);
            setClonesList([]);
        }
    }, [currentUser?._id]);

    const spouseOptions = clonesList
        ?.filter(item => item._id !== id)
        .map(item => ({
            value: item._id,
            label: `${item.data.name} ${item.data.surname}`,
        }));

    const parentOptions = clonesList
        ?.filter(item => item._id !== id)
        .map(item => ({
            value: item._id,
            label: `${item.data.name} ${item.data.surname}`,
        }));

    const handleDelete = async () => {
        try {
            await ApiService.delete(`/cms/clones/${id}`);
            toast.success("Clone successfully deleted!", {
                position: "top-center",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            navigate('/clone-list');
        } catch (error) {
            console.error("Error deleting clone:", error);
            toast.error("Failed to delete the clone. Please try again.", {
                position: "top-center",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    const handleImageChange = async (field, file) => {
        const base64 = await toBase64(file);
        setCloneData((prevState) => ({
            ...prevState,
            [field]: {
                name: file?.name,
                media: base64,
            },
        }));
    };

    const handleSave = async () => {
        try {
            const payload = {
                data: {
                    ...cloneData,
                    spouse: [cloneData?.spouse || '']
                },
            };

            setLoading(true);
            await ApiService.put(`/cms/clones/${id}`, payload);
            setLoading(false);
            toast.success("Clone updated successfully!", {
                position: "top-center",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } catch (error) {
            console.error("Error saving clone:", error);
            setLoading(false);
            toast.error("Failed to update the clone. Please try again.", {
                position: "top-center",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    };

    useEffect(() => {
        getClone();
        getClones();
    }, []);

    const [cloneConflict, setCloneConflict] = useState(false);
    const [conflictName, setConflictName] = useState('');

    const checkConflicts = () => {
        const spouseId = cloneData.spouse;
        const parentIds = cloneData.child_of;

        const isSpouseInParents = parentIds?.includes(spouseId);
        const isParentInSpouse = parentIds?.some(parentId => parentId === spouseId);

        if (isSpouseInParents) {
            const spouse = clonesList.find(item => item._id === spouseId);
            setConflictName(spouse ? `${spouse.data.name} ${spouse.data.surname}` : '');
        }

        if (isSpouseInParents || isParentInSpouse) {
            setCloneConflict(true)
        } else {
            setCloneConflict(false)
        }
    };

    const handleInputChange = (field, value) => {
        setCloneData((prevState) => {
            const updatedData = {
                ...prevState,
                [field]: value,
            };

            return updatedData;
        });
    };

    useEffect(() => {
        checkConflicts(cloneData);
    }, [cloneData.spouse, cloneData.child_of]);


    return (
        <div className="tyn-root clone">
            <Loading status={loading} blur={true}>
                <Nav />
                <div className="tyn-content tyn-add-clone">
                    <div className="tyn-main tyn-content-inner" id="tynMain">
                        <div className="container">
                            <div className="header d-flex align-items-center justify-content-between px-5 py-4" style={{ background: "white", borderRadius: "1rem" }}>
                                <div>
                                    <h3 className="text-capitalize center">
                                        Edit {cloneData?.name} {cloneData?.surname}
                                    </h3>
                                </div>
                                <div className="buttons d-flex">
                                    <span className="btn btn-primary me-2" aria-label="Save changes" onClick={handleSave}>
                                        Save
                                    </span>
                                    <Link className="btn btn-light" to={'/clone-list'} aria-label="Cancel and back to clone list">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" stroke="currentColor" strokeWidth="0.3" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                        </svg>
                                        Clone List
                                    </Link>
                                </div>
                                <div className="back-btn-mobile" style={{ display: 'none' }}>
                                    <Link className="btn btn-light" to={'/clone-list'} aria-label="Back to list">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
                                            <path fillRule="evenodd" stroke="currentColor" strokeWidth="0.3" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                                        </svg>
                                    </Link>
                                </div>
                            </div>

                            <div className="tyn-profile">
                                <div className="tyn-profile-head my-4">
                                    <div className="tyn-profile-cover new-cover">
                                        <img
                                            className="tyn-profile-cover-image"
                                            src={cloneData?.cover?.media || '/images/cover/banner-default.png'}
                                            alt=""
                                        />
                                        <label htmlFor="cover-upload" className="upload-icon-label">
                                            Change Cover
                                            <input
                                                type="file"
                                                accept="image/*"
                                                id="cover-upload"
                                                className="cover-upload-input"
                                                onChange={(e) => handleImageChange('cover', e.target.files[0])}
                                            />
                                        </label>
                                        <label htmlFor="cover-upload" className="upload-icon-label mobile-upload">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-upload" viewBox="0 0 16 16">
                                                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
                                            </svg>
                                        </label>
                                    </div>

                                    <div className="tyn-profile-info">
                                        <div className="tyn-media-group align-items-start">
                                            <div className="tyn-media tyn-media-bordered tyn-size-4xl tyn-profile-avatar">
                                                <img src={cloneData?.picture?.media || '/images/avatar/avatar-default.jpg'} alt="" />
                                                {/*  desktop */}
                                                <label htmlFor="avatar-upload" className="avatar-upload-label">
                                                    Change
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        id="avatar-upload"
                                                        className="avatar-upload-input"
                                                        onChange={(e) => handleImageChange('picture', e.target.files[0])}
                                                    />
                                                </label>
                                                {/*  mobile */}
                                                <label htmlFor="avatar-upload" className="avatar-upload-label mobile-upload">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-upload" viewBox="0 0 16 16">
                                                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
                                                        <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" />
                                                    </svg>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="tyn-profile-nav">
                                    {/* DESKTOP */}
                                    <ul className="nav nav-tabs nav-tabs-line">
                                        {tabs.map((tab) => (
                                            <li className="nav-item" key={tab.id}>
                                                <button
                                                    className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                                                    onClick={() => setActiveTab(tab.id)}
                                                >
                                                    {tab.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                    {/* MOBILE */}
                                    <div className="dropdown d-sm-none">
                                        <button
                                            className="btn btn-primary dropdown-toggle"
                                            type="button"
                                            id="tabDropdown"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                        >
                                            {tabs.find((tab) => tab.id === activeTab)?.label || "Select"}
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-caret-down-fill" viewBox="0 0 16 16">
                                                <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                                            </svg>
                                        </button>
                                        <ul className="dropdown-menu">
                                            {tabs.map((tab) => (
                                                <li key={tab.id}>
                                                    <button
                                                        className={`dropdown-item ${activeTab === tab.id ? "active" : ""}`}
                                                        onClick={() => setActiveTab(tab.id)}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <div className="tyn-profile-details edit-clone-details" style={{ background: "white" }}>
                                    <div className="tab-content">

                                        <div className={`tab-pane fade ${activeTab === "profile-edit" ? "show active" : ""}`} id="profile-edit" tabIndex="0">
                                            <div className="row gy-5">
                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Personal Information</h6>
                                                            <div className="tyn-subtext">Edit personal info</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">
                                                                {[
                                                                    { id: 'name', label: 'First Name *' },
                                                                    { id: 'surname', label: 'Last Name *' },
                                                                    { id: 'birthday', label: 'Birthday *', type: 'date' },
                                                                ]?.map((field) => (
                                                                    <div className="col-lg-6" key={field.id}>
                                                                        <div className="form-group">
                                                                            <label
                                                                                className="form-label"
                                                                                htmlFor={field.id}
                                                                            >
                                                                                {field.label}
                                                                            </label>
                                                                            <div className="form-control-wrap">
                                                                                <input
                                                                                    type={field.type || 'text'}
                                                                                    className="form-control"
                                                                                    id={field.id}
                                                                                    placeholder={field.label}
                                                                                    value={cloneData[field.id]}
                                                                                    onChange={(e) =>
                                                                                        handleInputChange(
                                                                                            field.id,
                                                                                            e.target.value
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="city">City of birth</label>
                                                                        <div className="form-control-wrap">
                                                                            <input type="text" className="form-control" id="city" placeholder="City" value={cloneData?.city} onChange={(e) => handleInputChange('city', e.target.value)} />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="country">
                                                                            Country
                                                                        </label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="country"
                                                                                name="country"
                                                                                value={countryOptions.find(option => option.value === cloneData?.country)}
                                                                                onChange={(selectedOption) =>
                                                                                    handleInputChange('country', selectedOption?.value || '')
                                                                                }
                                                                                options={countryOptions}
                                                                                placeholder="Select Country"
                                                                                isClearable
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="gender">
                                                                            Gender
                                                                        </label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="gender"
                                                                                value={genderOptions.find(option => option.value === cloneData?.gender)}
                                                                                onChange={(selectedOption) =>
                                                                                    handleInputChange('gender', selectedOption?.value || '')
                                                                                }
                                                                                options={genderOptions}
                                                                                placeholder="Select Gender"
                                                                                isClearable
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="spouse">Spouse</label>
                                                                        <div className="form-control-wrap">
                                                                            <Select id="spouse" value={spouseOptions.find(option => {
                                                                                const spouse = cloneData?.spouse?.length > 0 && cloneData?.spouse[0]?._id ? cloneData.spouse[0]._id : cloneData.spouse;
                                                                                return option.value === spouse;
                                                                            })}
                                                                                onChange={(selectedOption) => handleInputChange('spouse', selectedOption?.value)}
                                                                                options={spouseOptions}
                                                                                placeholder="Select Spouse"
                                                                                isClearable
                                                                            />
                                                                        </div>

                                                                        {/* <Banner /> */}
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="parents">Parents</label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="parents"
                                                                                value={cloneData?.child_of?.map(parentId => {
                                                                                    const parent = clonesList.find(item => item._id === (parentId?._id || parentId));
                                                                                    return parent ? { value: parent._id, label: `${parent.data.name} ${parent.data.surname}` } : null;
                                                                                }).filter(Boolean)}
                                                                                onChange={(selectedOptions) => {
                                                                                    const selectedParentIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
                                                                                    handleInputChange('child_of', selectedParentIds);
                                                                                }}
                                                                                options={parentOptions}
                                                                                placeholder="Select Parents"
                                                                                isMulti
                                                                                isClearable
                                                                            />
                                                                        </div>
                                                                        {cloneConflict && (
                                                                            <div className="warning-text">
                                                                                <div className="row">
                                                                                    <div className="col-12 d-flex align-items-center text">
                                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#E11D48" className="bi bi-exclamation-circle" viewBox="0 0 16 16">
                                                                                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                                                                                            <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                                                                                        </svg>
                                                                                        <p className='mb-0'>
                                                                                            <span>{conflictName}</span> can't be both a spouse and a parent.
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>



                                                                {/* Language */}
                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="language">
                                                                            Language spoken
                                                                        </label>
                                                                        <div className="form-control-wrap">
                                                                            <Select
                                                                                id="language"
                                                                                value={languageOptions.find(option => option.value === cloneData?.language)}
                                                                                onChange={(selectedOption) =>
                                                                                    handleInputChange('language', selectedOption?.value || '')
                                                                                }
                                                                                options={languageOptions}
                                                                                placeholder="Select Language"
                                                                                isClearable
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>


                                                                {/* General description */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label
                                                                            className="form-label"
                                                                            htmlFor="description"
                                                                        >
                                                                            General Description
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="description"
                                                                            placeholder="Description..."
                                                                            rows="4"
                                                                            value={cloneData?.description}
                                                                            onChange={(e) =>
                                                                                handleInputChange('description', e.target.value)
                                                                            }
                                                                        ></textarea>
                                                                    </div>
                                                                </div>


                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>


                                        <div className={`tab-pane fade ${activeTab === "physical-edit" ? "show active" : ""}`} id="physical-edit" tabIndex="0">
                                            <div className="row gy-5">
                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Physical Information</h6>
                                                            <div className="tyn-subtext">Edit physical attributes</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="height">
                                                                            Height (cm)
                                                                        </label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            id="height"
                                                                            placeholder="Height in cm"
                                                                            value={cloneData?.height || ''}
                                                                            onChange={(e) => handleInputChange('height', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="weight">
                                                                            Weight (kg)
                                                                        </label>
                                                                        <input
                                                                            type="number"
                                                                            className="form-control"
                                                                            id="weight"
                                                                            placeholder="Weight in kg"
                                                                            value={cloneData?.weight || ''}
                                                                            onChange={(e) => handleInputChange('weight', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="eye-color">
                                                                            Eye Color
                                                                        </label>
                                                                        <Select
                                                                            id="eye-color"
                                                                            value={eyeColorOptions.find(option => option.value === cloneData?.eyecolor)}
                                                                            onChange={(selectedOption) =>
                                                                                handleInputChange('eyecolor', selectedOption?.value || '')
                                                                            }
                                                                            options={eyeColorOptions}
                                                                            placeholder="Select Eye Color"
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="hair-color">
                                                                            Hair Color
                                                                        </label>
                                                                        <Select
                                                                            id="hair-color"
                                                                            value={hairColorOptions.find(option => option.value === cloneData?.haircolor)}
                                                                            onChange={(selectedOption) =>
                                                                                handleInputChange('haircolor', selectedOption?.value || '')
                                                                            }
                                                                            options={hairColorOptions}
                                                                            placeholder="Select Hair Color"
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="body-type">
                                                                            Body Type
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            id="body-type"
                                                                            placeholder="Body type (e.g., athletic, slim)"
                                                                            value={cloneData?.bodytype || ''}
                                                                            onChange={(e) => handleInputChange('bodytype', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="ethnicity">
                                                                            Origin/Nationality
                                                                        </label>
                                                                        <Select
                                                                            id="ethnicity"
                                                                            value={ethnicityOptions.find(option => option.value === cloneData?.ethnicity)}
                                                                            onChange={(selectedOption) =>
                                                                                handleInputChange('ethnicity', selectedOption?.value || '')
                                                                            }
                                                                            options={ethnicityOptions}
                                                                            placeholder="Select Nationality"
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="distinctive-marks">
                                                                            Distinctive Marks
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="distinctive-marks"
                                                                            placeholder="Distinctive marks (e.g., tattoos, scars)"
                                                                            rows="3"
                                                                            value={cloneData?.distinctivemarks || ''}
                                                                            onChange={(e) => handleInputChange('distinctivemarks', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>



                                                                <div className="col-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="other-physical-traits">
                                                                            Other Physical Traits
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="other-physical-traits"
                                                                            placeholder="Other physical traits"
                                                                            rows="3"
                                                                            value={cloneData?.otherphysicaltraits || ''}
                                                                            onChange={(e) => handleInputChange('otherphysicaltraits', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`tab-pane fade ${activeTab === "character-edit" ? "show active" : ""}`} id="character-edit" tabIndex="0">
                                            <div className="row gy-5">
                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Character Information</h6>
                                                            <div className="tyn-subtext">Edit character traits and behaviors</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">

                                                                {/* Personality Traits */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="personality-traits">
                                                                            Personality Traits
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="personality-traits"
                                                                            placeholder="Describe personality traits (e.g., introverted, optimistic)"
                                                                            rows="4"
                                                                            value={cloneData?.personalitytraits || ''}
                                                                            onChange={(e) => handleInputChange('personalitytraits', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Behaviors */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="behaviors">
                                                                            Typical Behaviors
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="behaviors"
                                                                            placeholder="Describe typical behaviors (e.g., punctual, risk-taking)"
                                                                            rows="4"
                                                                            value={cloneData?.behaviors || ''}
                                                                            onChange={(e) => handleInputChange('behaviors', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Interests */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="interests">
                                                                            Interests
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="interests"
                                                                            placeholder="List interests (e.g., reading, hiking, technology)"
                                                                            rows="4"
                                                                            value={cloneData?.interests || ''}
                                                                            onChange={(e) => handleInputChange('interests', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Strengths */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="strengths">
                                                                            Strengths
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="strengths"
                                                                            placeholder="List strengths (e.g., creativity, leadership)"
                                                                            rows="4"
                                                                            value={cloneData?.strengths || ''}
                                                                            onChange={(e) => handleInputChange('strengths', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Weaknesses */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="weaknesses">
                                                                            Weaknesses
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="weaknesses"
                                                                            placeholder="List weaknesses (e.g., impatience, shyness)"
                                                                            rows="4"
                                                                            value={cloneData?.weaknesses || ''}
                                                                            onChange={(e) => handleInputChange('weaknesses', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Motivation */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="motivations">
                                                                            Motivations
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="motivations"
                                                                            placeholder="Describe motivations (e.g., family, career goals)"
                                                                            rows="4"
                                                                            value={cloneData?.motivations || ''}
                                                                            onChange={(e) => handleInputChange('motivations', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Emotional Patterns */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="emotional-patterns">
                                                                            Emotional Patterns
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="emotional-patterns"
                                                                            placeholder="Describe emotional patterns (e.g., calm under pressure, easily stressed)"
                                                                            rows="4"
                                                                            value={cloneData?.emotionalpatterns || ''}
                                                                            onChange={(e) => handleInputChange('emotionalpatterns', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Social Preferences */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="social-preferences">
                                                                            Social Preferences
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="social-preferences"
                                                                            placeholder="Describe social preferences (e.g., outgoing, prefers small groups)"
                                                                            rows="4"
                                                                            value={cloneData?.socialpreferences || ''}
                                                                            onChange={(e) => handleInputChange('socialpreferences', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                {/* Coping Mechanisms */}
                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="coping-mechanisms">
                                                                            Coping Mechanisms
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="coping-mechanisms"
                                                                            placeholder="Describe coping mechanisms (e.g., meditation, talking to friends)"
                                                                            rows="4"
                                                                            value={cloneData?.copingmechanisms || ''}
                                                                            onChange={(e) => handleInputChange('copingmechanisms', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`tab-pane fade ${activeTab === "job-edit" ? "show active" : ""}`} id="job-edit" tabIndex="0">
                                            <div className="row gy-5">
                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Job Information</h6>
                                                            <div className="tyn-subtext">Edit professional details</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="job-title">
                                                                            Occupation
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            id="job-title"
                                                                            placeholder="e.g., Software Engineer"
                                                                            value={cloneData?.jobtitle || ''}
                                                                            onChange={(e) => handleInputChange('jobtitle', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="employment-status">
                                                                            Work Status
                                                                        </label>
                                                                        <Select
                                                                            id="employment-status"
                                                                            value={employmentStatusOptions.find(option => option.value === cloneData?.employmentstatus)}
                                                                            onChange={(selectedOption) =>
                                                                                handleInputChange('employmentstatus', selectedOption?.value || '')
                                                                            }
                                                                            options={employmentStatusOptions}
                                                                            placeholder="Select Work Status"
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="experience-level">
                                                                            Experience Level
                                                                        </label>
                                                                        <Select
                                                                            id="experience-level"
                                                                            value={experienceOptions.find(option => option.value === cloneData?.experiencelevel)}
                                                                            onChange={(selectedOption) =>
                                                                                handleInputChange('experiencelevel', selectedOption?.value || '')
                                                                            }
                                                                            options={experienceOptions}
                                                                            placeholder="Select Experience Level"
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-6">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="industry">
                                                                            Field / Sector
                                                                        </label>
                                                                        <Select
                                                                            id="industry"
                                                                            value={industryOptions.find(option => option.value === cloneData?.industry)}
                                                                            onChange={(selectedOption) =>
                                                                                handleInputChange('industry', selectedOption?.value || '')
                                                                            }
                                                                            options={industryOptions}
                                                                            placeholder="Select Industry"
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="job-description">
                                                                            Role Description / Responsibilities
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="job-description"
                                                                            placeholder="Describe the job role and responsibilities"
                                                                            rows="4"
                                                                            value={cloneData?.jobdescription || ''}
                                                                            onChange={(e) => handleInputChange('jobdescription', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="skills">
                                                                            Skills / Competencies
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="skills"
                                                                            placeholder="List skills relevant to the job"
                                                                            rows="3"
                                                                            value={cloneData?.skills || ''}
                                                                            onChange={(e) => handleInputChange('skills', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`tab-pane fade ${activeTab === "hobbies-edit" ? "show active" : ""}`} id="hobbies-edit" tabIndex="0">
                                            <div className="row gy-5">
                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Hobbies and Interests</h6>
                                                            <div className="tyn-subtext">Edit hobbies and personal interests</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="hobbies-select">
                                                                            Hobbies
                                                                        </label>
                                                                        <Select
                                                                            id="hobbies-select"
                                                                            value={hobbiesOptions.filter(option => cloneData?.hobbies?.includes(option?.value))}
                                                                            onChange={(selectedOptions) =>
                                                                                handleInputChange(
                                                                                    'hobbies',
                                                                                    selectedOptions ? selectedOptions.map(option => option.value) : []
                                                                                )
                                                                            }
                                                                            options={hobbiesOptions}
                                                                            placeholder="Select Hobbies"
                                                                            isMulti
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="custom-hobby">
                                                                            Add Another/Custom Hobby
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            id="custom-hobby"
                                                                            placeholder="e.g., Calligraphy, Gardening"
                                                                            value={cloneData?.customHobby || ''}
                                                                            onChange={(e) => handleInputChange('customHobby', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="hobbies-description">
                                                                            Description of Hobbies
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="hobbies-description"
                                                                            placeholder="Provide more details about the hobbies or interests"
                                                                            rows="4"
                                                                            value={cloneData?.hobbiesdescription || ''}
                                                                            onChange={(e) => handleInputChange('hobbiesdescription', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`tab-pane fade ${activeTab === "speech-edit" ? "show active" : ""}`} id="speech-edit" tabIndex="0">
                                            <div className="row gy-5">
                                                <div className="col-12">
                                                    <div className="row gy-4">
                                                        <div className="col-lg-3">
                                                            <h6>Speaking Style</h6>
                                                            <div className="tyn-subtext">Edit communication and speaking characteristics</div>
                                                        </div>
                                                        <div className="col-lg-9">
                                                            <div className="row g-gs">

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="speaking-style-select">
                                                                            Speaking Style
                                                                        </label>
                                                                        <Select
                                                                            id="speaking-style-select"
                                                                            value={speakingStyleOptions.filter(option => cloneData?.speakingstyle?.includes(option.value))}
                                                                            onChange={(selectedOptions) =>
                                                                                handleInputChange(
                                                                                    'speakingstyle',
                                                                                    selectedOptions ? selectedOptions.map(option => option.value) : []
                                                                                )
                                                                            }
                                                                            options={speakingStyleOptions}
                                                                            placeholder="Select Speaking Styles"
                                                                            isMulti
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="speaking-style-description">
                                                                            Speaking Style Description
                                                                        </label>
                                                                        <textarea
                                                                            className="form-control"
                                                                            id="speaking-style-description"
                                                                            placeholder="Provide more details about the speaking style"
                                                                            rows="4"
                                                                            value={cloneData?.speakingstyledescription || ''}
                                                                            onChange={(e) => handleInputChange('speakingstyledescription', e.target.value)}
                                                                        ></textarea>
                                                                    </div>
                                                                </div>

                                                                <div className="col-lg-12">
                                                                    <div className="form-group">
                                                                        <label className="form-label" htmlFor="custom-speaking-style">
                                                                            Add a Custom Speaking Style
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control"
                                                                            id="custom-speaking-style"
                                                                            placeholder="e.g., Sarcastic, Cheerful"
                                                                            value={cloneData?.customspeakingstyle || ''}
                                                                            onChange={(e) => handleInputChange('customspeakingstyle', e.target.value)}
                                                                        />
                                                                    </div>
                                                                </div>

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 mt-5">
                                            <div className="row">
                                                <div className="edit-clone-btns d-flex mt-4">
                                                    <ConfirmButton className="btn btn-danger delete-clone-btn py-2 fs-5"
                                                        redirect="/clone-list"
                                                        label="Delete"
                                                        title={`Delete ${cloneData?.name}`}
                                                        action={handleDelete}
                                                        message="Once you delete this clone, it cannot be restored. Are you sure you want to proceed?" />
                                                    <div className="d-flex justify-content-end buttons">
                                                        <button className="btn btn-primary me-2" disabled={disabled || cloneConflict} onClick={handleSave} aria-label="Confirm edits">Save Edits</button>
                                                        <button className="btn btn-light" disabled={disabled} onClick={() => navigate("/clone-list")} aria-label="Cancel and back to clones list">Cancel</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </Loading >
        </div >
    );
};

export default EditClone;
